import { toJpeg } from "html-to-image"

/**
 * Marks a rendered message built from the user's personal sources (mail, files, chats). Such
 * content is owner-only and must never reach an evidence blob (spec 6.6), so a screen that shows
 * any of it is not captured.
 */
export const PERSONAL_CONTENT_ATTRIBUTE = "data-personal-content"

/** Spread onto a message's root element: the marker attribute when it is personal, nothing otherwise. */
export function personalContentAttribute(isPersonal: boolean): {
  [PERSONAL_CONTENT_ATTRIBUTE]?: "true"
} {
  return isPersonal ? { [PERSONAL_CONTENT_ATTRIBUTE]: "true" } : {}
}

/** True while any message built from personal sources is rendered on the page. */
export function personalContentOnScreen(): boolean {
  if (typeof document === "undefined") return false
  return document.querySelector(`[${PERSONAL_CONTENT_ATTRIBUTE}]`) !== null
}

/**
 * Best-effort capture of the current app screen for feedback attachments.
 *
 * Captures the `#root` element only — Radix portals (our dialog, toasts,
 * tooltips, etc.) render as siblings of `#root` under `<body>` so they are
 * naturally excluded. We additionally filter out any element with
 * `role="dialog"`, `[data-sonner-toaster]`, or `[data-screenshot-skip]`
 * as a safety net in case a UI chrome element is mounted inside `#root`.
 *
 * Returns JPEG (not PNG) to keep file size reasonable on long pages.
 * Returns `null` when the screen shows personal-source content (never captured) or if capture fails for any reason — the feedback flow
 * continues without the screenshot.
 */
export async function captureScreen(): Promise<{ blob: Blob; previewUrl: string } | null> {
  const root = typeof document !== "undefined" ? document.getElementById("root") : null
  if (!root) return null
  // Fail closed: a screenshot of a screen that shows personal content is never taken.
  if (personalContentOnScreen()) return null

  try {
    const dataUrl = await toJpeg(root, {
      quality: 0.85,
      // Don't upscale for hi-DPI displays — keeps the file size sane.
      pixelRatio: 1,
      cacheBust: true,
      // Some embedded third-party resources (avatars, external images) can
      // fail CORS and break the whole capture. Skip them.
      skipAutoScale: true,
      filter: (node) => {
        if (!(node instanceof HTMLElement)) return true
        if (node.getAttribute("role") === "dialog") return false
        if (node.hasAttribute("data-sonner-toaster")) return false
        if (node.hasAttribute("data-screenshot-skip")) return false
        if (node.hasAttribute(PERSONAL_CONTENT_ATTRIBUTE)) return false
        return true
      },
    })

    const response = await fetch(dataUrl)
    const blob = await response.blob()
    const previewUrl = URL.createObjectURL(blob)
    return { blob, previewUrl }
  } catch (err) {
    // Swallow — feedback flow continues without the screenshot.
    // eslint-disable-next-line no-console
    console.warn("[feedback] screen capture failed", err)
    return null
  }
}
